import React from "react";
import { healthColorForScore } from "@/lib/pillarTone";
import { PillarHeatMapV2 } from "@/components/PillarHeatMapV2";
import { DiagnosticHistoryV2 } from "@/components/DiagnosticHistoryV2";

// ── Executive Clarity helpers — 80/60 bands (Stage B) ──
const RISK_COLORS = {
  "Healthy": "text-[#166534] bg-[#f0fdf4] border-[#bbf7d0]",
  "Caution": "text-[#92400e] bg-[#fffbeb] border-[#fde68a]",
  "High Risk": "text-[#991b1b] bg-[#fef2f2] border-[#fecaca]",
};

export const riskBadgeClass = (label) => RISK_COLORS[label] || "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]";

export { healthColorForScore };

export function overallRiskBand(score) {
  if (score == null) return { label: "Not scored", bg: "bg-[#f3f4f6]", border: "border-[#e5e7eb]", text: "text-[#6b7280]" };
  if (score >= 80) return { label: "Healthy", bg: "bg-[#f0fdf4]", border: "border-[#bbf7d0]", text: "text-[#166534]" };
  if (score >= 60) return { label: "Caution", bg: "bg-[#fffbeb]", border: "border-[#fde68a]", text: "text-[#92400e]" };
  return { label: "High Risk", bg: "bg-[#fef2f2]", border: "border-[#fecaca]", text: "text-[#991b1b]" };
}


export const OverallRiskBanner = ({ score, riskBand, provisional = false, testid = "portfolio-overall-risk" }) => {
  const band = overallRiskBand(score);
  return (
    <div className={`rounded-xl border ${band.border} ${band.bg} px-5 py-4 flex items-center justify-between gap-3`} data-testid={testid}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className={`text-[10px] uppercase tracking-wider font-bold ${band.text}`}>Overall Risk Level</div>
          {provisional && score != null && (
            <span data-testid={`${testid}-provisional`} className="rounded-full border border-[#fde047] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#a16207]">
              In Progress · Provisional
            </span>
          )}
        </div>
        <div className={`text-2xl font-bold mt-0.5 ${band.text}`}>{band.label}</div>
        {riskBand && <div className={`text-[12px] mt-0.5 ${band.text} opacity-80`}>{riskBand}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[10px] uppercase tracking-wider font-semibold ${band.text}`}>Score</div>
        <div className={`text-3xl font-bold ${band.text}`}>{score != null ? score : "—"}<span className="text-base font-normal opacity-60">/100</span></div>
      </div>
    </div>
  );
};

// Portfolio view of a school's pillars — same v2 component as the School experience, view-only.
export const PillarHeatMap = ({ pillars, onReview }) => (
  <PillarHeatMapV2 viewer="portfolio" pillars={pillars} gridTestId="portfolio-pillar-tiles"
    ids={{ card: (p) => `portfolio-pillar-tile-${p.order}`, open: (p) => `portfolio-pillar-open-${p.id}` }}
    onOpen={onReview || null} />
);


// Read-only Diagnostic History (v2) — school line + this Portfolio's final assessment overlay. NO "Start New", NO "Download PDF".
export const DiagnosticHistory = ({ history, tenantId }) => (
  <DiagnosticHistoryV2 history={history} viewer="portfolio"
    ids={{ section: "portfolio-history-section", chart: "portfolio-trend-chart", list: "portfolio-history-list", row: (h) => `portfolio-history-${h.id}` }}
    compareHref={(h) => (h.period_id && tenantId ? `/portfolio/compare/${h.period_id}/${tenantId}` : null)} />
);
